/**
 * Admin Docs Index (Iter 9.29) - clickable view of every Markdown spec under
 * /app/memory/. Reads via GET /api/admin/docs/list and GET /api/admin/docs/{filename}.
 * Mounted at /admin/docs.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Loader2, Search, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("session_token")}`,
});

const FRIENDLY_LABELS = {
  "PRD.md": "Product Requirements (PRD)",
  "SHELSEA_DECK_GUIDE.md": "Shelsea Pilot Deck Guide",
  "PRODUCT_TRUTH_TABLE.md": "Product Truth Table - Every Action, Metric, Insight",
  "MAKEUP_HOST_PLAYBOOK.md": "Makeup Host Playbook",
  "ADMIN_COPY_CONVERSION_BACKLOG.md": "Admin Copy Conversion Backlog",
  "FULL_TEST_MATRIX.md": "Full Test Matrix",
  "BEEHIIV_MASTER.md": "Beehiiv - Master Reference (single source)",
  "USER_FLOWS.md": "User Flows",
  "CHANGELOG.md": "Changelog",
  "ROADMAP.md": "Roadmap",
};

export default function AdminDocsIndexPage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [content, setContent] = useState("");
  const [contentLoading, setContentLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/docs/list`, { headers: headers() });
      if (r.ok) {
        const d = await r.json();
        setDocs(d.docs || []);
        if (d.docs?.length && !active) {
          await openDoc(d.docs[0].filename);
        }
      }
    } finally { setLoading(false); }
  };

  const openDoc = async (filename) => {
    setActive(filename);
    setContentLoading(true);
    try {
      const r = await fetch(`${API}/admin/docs/${encodeURIComponent(filename)}`, { headers: headers() });
      if (r.ok) {
        const d = await r.json();
        setContent(d.content || "");
      } else {
        setContent("Could not load document.");
      }
    } finally { setContentLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = docs.filter(d => {
    if (!search) return true;
    const lc = search.toLowerCase();
    return d.filename.toLowerCase().includes(lc) || (FRIENDLY_LABELS[d.filename] || "").toLowerCase().includes(lc);
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-docs-page">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sticky top-0 z-20">
        <div className="max-w-[1700px] mx-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <FileText className="w-5 h-5 text-indigo-600" />
          <h1 className="text-lg font-semibold">Documentation</h1>
          <span className="text-xs text-gray-500 ml-2">{docs.length} files</span>
        </div>
      </header>

      <main className="max-w-[1700px] mx-auto p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
          {/* Sidebar */}
          <aside className="bg-white rounded-xl border border-gray-200 p-3">
            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search docs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-8 text-xs"
                data-testid="docs-search"
              />
            </div>
            {loading ? (
              <div className="text-center py-6 text-xs text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              </div>
            ) : (
              <ul className="space-y-0.5 max-h-[70vh] overflow-y-auto" data-testid="docs-list">
                {filtered.map(d => (
                  <li key={d.filename}>
                    <button
                      onClick={() => openDoc(d.filename)}
                      className={`w-full text-left px-2 py-2 rounded text-xs transition-colors ${
                        active === d.filename
                          ? "bg-indigo-50 text-indigo-700 font-semibold"
                          : "hover:bg-gray-100 text-gray-700"
                      }`}
                      data-testid={`docs-link-${d.filename}`}
                    >
                      <div className="font-medium">{FRIENDLY_LABELS[d.filename] || d.filename}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5">{d.filename} · {d.size_kb}kb</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Reader */}
          <article className="bg-white rounded-xl border border-gray-200 p-5 min-h-[60vh]">
            {contentLoading ? (
              <div className="text-center py-10 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : !active ? (
              <p className="text-sm text-gray-400">Select a document to read.</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-700">{active}</h2>
                  <a
                    href={`${API}/admin/docs/${encodeURIComponent(active)}/raw`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-1"
                    data-testid="docs-raw-link"
                  >
                    Raw <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <pre
                  className="text-[12px] text-gray-800 whitespace-pre-wrap font-sans leading-relaxed"
                  data-testid="docs-content"
                >
                  {content}
                </pre>
              </>
            )}
          </article>
        </div>
      </main>
    </div>
  );
}
